<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
      <div class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Attendance Tracking</h1>
          <p class="text-base text-gray-600 dark:text-gray-400 mt-2">Monitor member check-ins and attendance patterns</p>
        </div>
        <div class="flex items-center gap-2">
          <input v-model="memberSearch" type="text" class="form-input" placeholder="Search member..." />
          <button class="btn btn-primary" @click="openCheckIn">Check In Member</button>
        </div>
      </div>

      <!-- Attendance Statistics -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Today's Check-ins</p>
                <p class="text-2xl font-bold text-green-600">{{ todayCheckins }}</p>
              </div>
              <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Active Now</p>
                <p class="text-2xl font-bold text-blue-600">{{ activeNow }}</p>
              </div>
              <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Weekly Average</p>
                <p class="text-2xl font-bold text-purple-600">{{ weeklyAverage }}</p>
              </div>
              <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Total Members</p>
                <p class="text-2xl font-bold text-orange-600">{{ totalMembers }}</p>
              </div>
              <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Check-in Section -->
      <div class="card dark:bg-gray-900 mb-6">
        <div class="card-header dark:border-gray-800">
          <h2 class="text-xl font-semibold">Quick Check-in</h2>
        </div>
        <div class="card-body">
          <div class="flex flex-col sm:flex-row gap-4">
            <div class="flex-1">
              <label class="form-label">Member ID or Name</label>
              <input v-model="quickCheckIn" type="text" class="form-input" placeholder="Enter member ID or name" @keyup.enter="performQuickCheckIn" />
            </div>
            <div class="flex items-end">
              <button class="btn btn-primary" @click="performQuickCheckIn" :disabled="!quickCheckIn.trim()">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Check In
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Scan Result Dialog -->
      <div v-if="scanResultOpen" class="modal-overlay" @click="scanResultOpen = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">Member Scan Result</h3>
              <button class="btn btn-outline btn-sm" @click="scanResultOpen = false">Close</button>
            </div>
          </div>
          <div class="modal-body">
            <div v-if="scanResult" class="space-y-3">
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Member ID:</span>
                <span class="font-medium">{{ scanResult.memberId }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Name:</span>
                <span class="font-medium">{{ scanResult.name }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Phone:</span>
                <span class="font-medium">{{ scanResult.phone }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Plan:</span>
                <span class="font-medium">{{ scanResult.plan }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Next Payment:</span>
                <span class="font-medium">{{ scanResult.nextPayment || 'N/A' }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Status:</span>
                <span :class="scanResult.statusBadge" class="px-2 py-0.5 rounded text-xs font-medium">{{ scanResult.statusText }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Entry Today:</span>
                <span class="font-medium text-green-700">{{ scanResult.notEnteredToday ? 'Not entered before today' : 'Already checked in today' }}</span>
              </div>
            </div>
            <div v-else class="text-sm text-red-600">Member not found.</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="scanResultOpen = false">Done</button>
          </div>
        </div>
      </div>

      <!-- Current Attendance -->
      <div class="card dark:bg-gray-900 mb-6">
        <div class="card-header dark:border-gray-800">
          <h2 class="text-xl font-semibold">Currently in Gym</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="attendance in currentAttendance" :key="attendance.id" 
                 class="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-800">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center">
                  <div class="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                    <span class="text-sm font-medium text-blue-600">{{ attendance.member.charAt(0) }}</span>
                  </div>
                  <div>
                    <div class="font-medium">{{ attendance.member }}</div>
                    <div class="text-sm text-gray-500">ID: {{ attendance.memberId }}</div>
                  </div>
                </div>
                <button class="btn btn-outline btn-sm" @click="checkOut(attendance)">
                  Check Out
                </button>
              </div>
              <div class="text-sm text-gray-600">
                <div>Check-in: {{ formatTime(attendance.checkInTime) }}</div>
                <div>Duration: {{ getDuration(attendance.checkInTime) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Attendance History -->
      <div class="card dark:bg-gray-900">
        <div class="card-header dark:border-gray-800">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold">Attendance History</h2>
            <div class="flex items-center gap-2">
              <input v-model="dateFilter" type="date" class="form-input" />
              <select v-model="statusFilter" class="form-input">
                <option value="">All Status</option>
                <option value="Checked In">Checked In</option>
                <option value="Checked Out">Checked Out</option>
              </select>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div class="overflow-x-auto">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th class="table-header-cell">Member</th>
                  <th class="table-header-cell">Check-in Time</th>
                  <th class="table-header-cell">Check-out Time</th>
                  <th class="table-header-cell">Duration</th>
                  <th class="table-header-cell">Status</th>
                  <th class="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr class="table-row" v-for="record in filteredAttendanceHistory" :key="record.id">
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                                          <span class="text-sm font-medium text-gray-600">{{ record.member_name.charAt(0) }}</span>
                </div>
                <span class="font-medium">{{ record.member_name }}</span>
                    </div>
                  </td>
                  <td class="table-cell">{{ formatDateTime(record.check_in_time) }}</td>
                  <td class="table-cell">{{ record.check_out_time ? formatDateTime(record.check_out_time) : '-' }}</td>
                  <td class="table-cell">{{ getDuration(record.check_in_time, record.check_out_time) }}</td>
                  <td class="table-cell">
                    <span :class="getStatusBadgeClass(record.status)">
                      {{ record.status }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <div class="flex gap-2">
                      <button class="btn btn-outline btn-sm" @click="viewDetails(record)">View</button>
                      <button v-if="record.status === 'Checked In'" class="btn btn-warning btn-sm" @click="checkOut(record)">Check Out</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Check-in Modal -->
      <div v-if="showCheckInModal" class="modal-overlay" @click="closeCheckInModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="text-lg font-semibold">Check In Member</h3>
          </div>
          <div class="modal-body">
            <div class="space-y-4">
              <div>
                <label class="form-label">Member</label>
                <select v-model="checkInForm.member" class="form-input">
                  <option v-for="member in availableMembers" :key="member.id" :value="member.name">
                    {{ member.name }} (ID: {{ member.id }})
                  </option>
                </select>
              </div>
              <div>
                <label class="form-label">Notes</label>
                <textarea v-model="checkInForm.notes" class="form-input" rows="3" placeholder="Optional notes..."></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline" @click="closeCheckInModal">Cancel</button>
            <button class="btn btn-primary" @click="performCheckIn">Check In</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    memberSearch: '',
    quickCheckIn: '',
    dateFilter: new Date().toISOString().split('T')[0],
    statusFilter: '',
    scanResultOpen: false,
    scanResult: null,
    showCheckInModal: false,
    checkInForm: {
      member: '',
      notes: ''
    },
    // Minimal member index for scan details
    membersIndex: [
      { memberId: 'M001', name: 'John Doe', phone: '+11123456789', plan: 'Premium', nextPayment: '2024-02-15', active: true, lastEntryDate: '2024-01-14' },
      { memberId: 'M002', name: 'Jane Smith', phone: '+11234567890', plan: 'VIP', nextPayment: '2024-02-03', active: true, lastEntryDate: '2024-01-15' },
      { memberId: 'M003', name: 'Mike Johnson', phone: '+11345678901', plan: 'Basic', nextPayment: '2024-01-21', active: false, lastEntryDate: '2024-01-10' }
    ],
    members: [
      { id: 'M001', name: 'John Doe', status: 'Active' },
      { id: 'M002', name: 'Jane Smith', status: 'Active' },
      { id: 'M003', name: 'Mike Johnson', status: 'Active' },
      { id: 'M004', name: 'Sarah Wilson', status: 'Active' },
      { id: 'M005', name: 'David Brown', status: 'Active' }
    ],
    attendanceHistory: [
      { id: 1, memberId: 'M001', member: 'John Doe', checkInTime: '2024-01-15T08:30:00', checkOutTime: '2024-01-15T10:15:00', status: 'Checked Out', notes: 'Morning workout' },
      { id: 2, memberId: 'M002', member: 'Jane Smith', checkInTime: '2024-01-15T09:00:00', checkOutTime: null, status: 'Checked In', notes: 'Yoga class' },
      { id: 3, memberId: 'M003', member: 'Mike Johnson', checkInTime: '2024-01-15T07:45:00', checkOutTime: '2024-01-15T09:30:00', status: 'Checked Out', notes: 'Cardio session' },
      { id: 4, memberId: 'M004', name: 'Sarah Wilson', checkInTime: '2024-01-14T18:00:00', checkOutTime: '2024-01-14T19:30:00', status: 'Checked Out', notes: 'Evening workout' },
      { id: 5, memberId: 'M005', name: 'David Brown', checkInTime: '2024-01-14T16:00:00', checkOutTime: '2024-01-14T17:45:00', status: 'Checked Out', notes: 'Strength training' }
    ]
  }),
  computed: {
    todayCheckins() {
      const today = new Date().toISOString().split('T')[0];
      return this.attendanceHistory.filter(record => 
        record.check_in_time && record.check_in_time.startsWith(today)
      ).length;
    },
    activeNow() {
      return this.attendanceHistory.filter(record => 
        record.status === 'checked_in'
      ).length;
    },
    weeklyAverage() {
      // Calculate weekly average (simplified)
      return Math.round(this.attendanceHistory.length / 7);
    },
    totalMembers() {
      return this.members.length;
    },
    currentAttendance() {
      return this.attendanceHistory.filter(record => 
        record.status === 'checked_in'
      );
    },
    filteredAttendanceHistory() {
      let filtered = this.attendanceHistory;
      
      if (this.dateFilter) {
        filtered = filtered.filter(record => 
          record.check_in_time && record.check_in_time.startsWith(this.dateFilter)
        );
      }
      
      if (this.statusFilter) {
        filtered = filtered.filter(record => 
          record.status === this.statusFilter
        );
      }
      
      return filtered;
    },
    availableMembers() {
      return this.members.filter(member => 
        !this.attendanceHistory.some(record => 
          record.member_id === member.id && record.status === 'checked_in'
        )
      );
    }
  },
  methods: {
    async loadAttendanceData() {
      try {
        const [membersRes, attendanceRes] = await Promise.all([
          fetch('http://localhost:4000/api/members'),
          fetch('http://localhost:4000/api/attendance')
        ]);

        if (membersRes.ok) {
          this.members = await membersRes.json();
        }
        if (attendanceRes.ok) {
          this.attendanceHistory = await attendanceRes.json();
        }
      } catch (error) {
        console.error('Error loading attendance data:', error);
        this.$bus.$emit('toast:show', { title: 'Failed to load attendance data', type: 'error' });
      }
    },

    openCheckIn() {
      this.showCheckInModal = true;
    },

    closeCheckInModal() {
      this.showCheckInModal = false;
      this.checkInForm = { member: '', notes: '' };
    },

    async performQuickCheckIn() {
      if (!this.quickCheckIn.trim()) return;
      
      try {
        const response = await fetch(`http://localhost:4000/api/members/scan/${this.quickCheckIn.trim()}`);
        if (response.ok) {
          this.scanResult = await response.json();
          this.scanResultOpen = true;
        } else {
          this.scanResult = null;
          this.scanResultOpen = true;
          this.$bus.$emit('toast:show', { title: 'Member not found', type: 'error' });
        }
      } catch (error) {
        console.error('Error scanning member:', error);
        this.$bus.$emit('toast:show', { title: 'Failed to scan member', type: 'error' });
      }
    },
    async performCheckIn() {
      const member = this.members.find(m => m.name === this.checkInForm.member);
      if (!member) return;
      
      try {
        const response = await fetch('http://localhost:4000/api/attendance', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            member_id: member.member_id,
            notes: this.checkInForm.notes
          })
        });

        if (response.ok) {
          const newRecord = await response.json();
          this.attendanceHistory.unshift(newRecord);
          this.$bus.$emit('toast:show', { title: `${member.name} checked in successfully`, type: 'success' });
          this.closeCheckInModal();
        } else {
          const error = await response.json();
          this.$bus.$emit('toast:show', { title: error.message || 'Failed to check in', type: 'error' });
        }
      } catch (error) {
        console.error('Error checking in:', error);
        this.$bus.$emit('toast:show', { title: 'Failed to check in', type: 'error' });
      }
    },
    async checkOut(record) {
      try {
        const response = await fetch(`http://localhost:4000/api/attendance/${record.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            check_out_time: new Date().toISOString(),
            notes: record.notes
          })
        });

        if (response.ok) {
          const updatedRecord = await response.json();
          const index = this.attendanceHistory.findIndex(r => r.id === record.id);
          if (index > -1) {
            this.attendanceHistory[index] = updatedRecord;
          }
          this.$bus.$emit('toast:show', { title: `${record.member_name} checked out successfully`, type: 'success' });
        } else {
          const error = await response.json();
          this.$bus.$emit('toast:show', { title: error.message || 'Failed to check out', type: 'error' });
        }
      } catch (error) {
        console.error('Error checking out:', error);
        this.$bus.$emit('toast:show', { title: 'Failed to check out', type: 'error' });
      }
    },
    viewDetails(record) {
      this.$bus.$emit('toast:show', { title: `Viewing attendance details for ${record.member_name}`, type: 'info' });
    },
    formatDateTime(dateTime) {
      return new Date(dateTime).toLocaleString();
    },
    formatTime(dateTime) {
      return new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    getDuration(checkInTime, checkOutTime = null) {
      if (!checkInTime) return 'N/A';
      const checkIn = new Date(checkInTime);
      const checkOut = checkOutTime ? new Date(checkOutTime) : new Date();
      const diffMs = checkOut - checkIn;
      const diffMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      
      if (hours > 0) {
        return `${hours}h ${mins}m`;
      }
      return `${mins}m`;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'checked_in':
          return 'badge badge-success';
        case 'checked_out':
          return 'badge badge-info';
        default:
          return 'badge badge-gray';
      }
    }
  },

  async created() {
    await this.loadAttendanceData();
  }
}
</script>
